import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { createClient } from '@/utils/supabase/client'
import { useCurrentWorkspace } from '@/hooks/useCurrentWorkspace'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ImageCropper } from '@/components/ImageCropper'
import { uploadImage } from '@/lib/storage'
import { HugeiconsIcon } from '@hugeicons/react'
import { Image01Icon, Add01Icon, Cancel01Icon } from '@hugeicons-pro/core-stroke-rounded'
import Image from 'next/image'
import { sendUAConversion } from '@/lib/measurement/ua-client'
import { Tables } from '@/utils/schema'

interface WorkspaceFormProps {
  onSuccess?: () => void
  onCancel?: () => void
}

const STEPS = [
  { title: 'Avatar', subtitle: 'Upload a workspace avatar' },
  { title: 'Workspace Name', subtitle: 'Enter your workspace name' },
  { title: 'Domain Name', subtitle: 'Specify your workspace domain name' },
  { title: 'Location', subtitle: 'Where is your workspace located?' },
  { title: 'Invite Members', subtitle: 'Invite team members (optional)' }
]

// Common email providers to exclude from domain prefill
const COMMON_EMAIL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'aol.com',
  'icloud.com',
  'proton.me',
  'protonmail.com',
  'mail.com',
  'zoho.com',
  'yandex.com',
  'me.com',
  'live.com',
  'msn.com'
]

export function WorkspaceForm({ onSuccess, onCancel }: WorkspaceFormProps) {
  const supabase = createClient()
  const { switchWorkspace } = useCurrentWorkspace()
  const [currentStep, setCurrentStep] = useState(1)
  const [displayName, setDisplayName] = useState('')
  const [domainName, setDomainName] = useState('')
  const [location, setLocation] = useState('')
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [showCropper, setShowCropper] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [inviteEmails, setInviteEmails] = useState<string[]>([])
  const [currentEmail, setCurrentEmail] = useState('')

  // Prefill domain from user's email on component mount
  useEffect(() => {
    const prefillDomainFromUserEmail = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser()
        
        if (error || !user?.email) return
        
        const emailParts = user.email.split('@')
        if (emailParts.length !== 2) return
        
        const domain = emailParts[1]
        
        // Only prefill if it's not a common email provider
        if (!COMMON_EMAIL_DOMAINS.includes(domain)) {
          setDomainName(domain)
        }
      } catch (error) {
        console.log('Error prefilling domain:', error)
      }
    }
    
    prefillDomainFromUserEmail()
  }, [supabase])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setPreview(URL.createObjectURL(selectedFile))
      setShowCropper(true)
    }
  }

  const handleCropComplete = async (croppedBlob: Blob) => {
    setShowCropper(false)
    const file = new File([croppedBlob], 'avatar.jpg', { type: 'image/jpeg' })
    setAvatarFile(file)
    setPreview(URL.createObjectURL(croppedBlob))
  }

  const handleAddEmail = () => {
    if (currentEmail && /^\S+@\S+\.\S+$/.test(currentEmail)) {
      setInviteEmails([...inviteEmails, currentEmail])
      setCurrentEmail('')
    } else {
      toast.error('Please enter a valid email address')
    }
  }

  const handleRemoveEmail = (email: string) => {
    setInviteEmails(inviteEmails.filter(e => e !== email))
  }

  const createStripeCustomer = async (workspaceId: string, userEmail: string) => {
    try {
      const response = await fetch('/api/stripe/create-workspace-customer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspaceId,
          email: userEmail,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create Stripe customer');
      }

      const data = await response.json();
      return data.stripeCustomerId;
    } catch (error) {
      console.log('Error creating Stripe customer:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isStepValid()) {
      toast.error(`Please complete the current step: ${STEPS[currentStep - 1]?.title || 'Current Step'}`)
      return
    }

    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1)
      return
    }

    setIsLoading(true)
    try {
      // Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError) throw userError
      if (!user || !user.email) {
        throw new Error('User not authenticated or missing email')
      }

      // Get the user's public profile
      const { data: publicProfileData, error: profileError } = await supabase
        .from('public_profiles')
        .select('id')
        .eq('auth_user_id', user.id)
        .single()
      
      if (profileError) throw profileError
      const publicProfile = publicProfileData as Pick<Tables<'public_profiles'>, 'id'> | null
      if (!publicProfile) {
        throw new Error('Public profile not found')
      }

      // Use the secure create_workspace function
      const { data: workspaceIdData, error } = await supabase
        .rpc('create_workspace', {
          p_creator_auth_id: user.id,
          p_creator_profile_id: publicProfile.id,
          p_display_name: displayName.trim(),
          p_domain_name: domainName.trim() || null,
          p_location: location.trim() || null,
        })

      if (error) throw error
      const workspaceId = workspaceIdData as string

      // Now that we're a member, upload the avatar if one was selected
      let avatarPath = null
      if (avatarFile) {
        try {
          avatarPath = await uploadImage(avatarFile, 'workspace-avatars')
          
          // Update the workspace with the avatar path
          const { error: updateError } = await supabase
            .from('workspaces')
            .update({ avatar_path: avatarPath })
            .eq('id', workspaceId)
            
          if (updateError) {
            console.log('Error updating workspace avatar:', updateError)
            // Non-blocking error, we'll continue
          }
        } catch (avatarError) {
          console.log('Error uploading avatar:', avatarError)
          // Non-blocking error, we'll continue
        }
      }

      // Create a Stripe customer for the workspace
      try {
        const stripeCustomerId = await createStripeCustomer(workspaceId, user.email);
        
        // Update the workspace with the Stripe customer ID
        const { error: updateError } = await supabase
          .from('workspaces')
          .update({ stripe_customer_id: stripeCustomerId })
          .eq('id', workspaceId);
          
        if (updateError) {
          console.log('Error updating workspace with Stripe customer ID:', updateError);
          // Non-blocking error, we'll continue
        }
      } catch (stripeError) {
        console.log('Error with Stripe customer creation:', stripeError);
        // Non-blocking error, we'll continue with the workspace creation
      }

      // Handle invites if they're present. Use the public API route so the
      // request runs with the user's auth context (the route verifies the
      // caller is owner/admin of the workspace before issuing the invite).
      if (inviteEmails.length > 0) {
        for (const email of inviteEmails) {
          try {
            const res = await fetch(`/api/invitations/create?workspaceId=${workspaceId}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email, role: 'member' }),
            })
            if (!res.ok) {
              const data = await res.json().catch(() => ({}))
              toast.error(`Failed to invite ${email}: ${data?.error || res.statusText}`)
            }
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to send invite'
            toast.error(`Failed to invite ${email}: ${msg}`)
          }
        }
      }


      toast.success('Workspace created successfully')
      sendUAConversion('sign_up', {
        user_id: user.id,
        user_name: user.email,
        description: displayName.trim() || 'Workspace created',
      })
      void fetch('/api/measurement/auth-conversion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_name: 'SignUp',
          workspace_id: workspaceId,
          description: displayName.trim() || 'Workspace created',
        }),
      })
      
      // Switch to the new workspace and refresh
      await switchWorkspace(workspaceId)
      
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.log('Error creating workspace:', error)
      toast.error('Failed to create workspace')
    } finally {
      setIsLoading(false)
    }
  }

  const isStepValid = () => {
    switch (currentStep) {
      case 1: return true; // Avatar is optional
      case 2: return displayName.trim() !== '';
      case 3: return true; // Domain name is optional
      case 4: return true; // Location is optional
      case 5: return true; // Invites are optional
      default: return false;
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Avatar
        return (
          <div className="flex flex-col items-center space-y-6">
            {showCropper && preview ? (
              <div className="w-full">
                <ImageCropper
                  imageUrl={preview}
                  onCropComplete={handleCropComplete}
                  onCancel={() => {
                    setShowCropper(false)
                    setPreview(null)
                  }}
                />
              </div>
            ) : (
              <>
                {preview ? (
                  <div className="overflow-hidden relative w-32 h-32 rounded-full">
                    <Image
                      src={preview}
                      alt="Avatar preview"
                      fill
                      className="object-cover"
                    />
                  </div>
                ) : (
                  <div className="flex justify-center items-center w-32 h-32 rounded-full border bg-atrium-content border-atrium-border">
                    <HugeiconsIcon icon={Image01Icon} className="w-10 h-10 text-atrium-black" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="avatar-upload"
                  disabled={isLoading}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('avatar-upload')?.click()}
                  disabled={isLoading}
                  className="disabled:opacity-50"
                >
                  {avatarFile ? 'Choose Different Image' : 'Choose Image'}
                </Button>
              </>
            )}
          </div>
        )

      case 2: // Workspace Name
        return (
          <div className="space-y-2">
            <Label htmlFor="displayName" className="text-atrium-black/80">Workspace name</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter workspace name"
              variant="white"
              required
              autoFocus
            />
          </div>
        )

      case 3: // Domain Name
        return (
          <div className="space-y-2">
            <Label htmlFor="domainName" className="text-atrium-black/80">Domain Name</Label>
            <Input
              id="domainName"
              value={domainName}
              onChange={(e) => setDomainName(e.target.value)}
              placeholder="Enter domain name"
              variant="white"
              autoFocus
            />
            <p className="mt-2 text-sm text-neutral-600">
              The domain used for your workspace&apos;s emails and website.
            </p>
          </div>
        )

      case 4: // Location
        return (
          <div className="space-y-2">
            <Label htmlFor="location" className="text-atrium-black/80">Location</Label>
            <Input
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Enter workspace location"
              variant="white"
              autoFocus
            />
            <p className="mt-2 text-sm text-neutral-600">
              The physical location or headquarters of your workspace.
            </p>
          </div>
        )

      case 5: // Invite Members (Optional)
        return (
          <div className="space-y-4">
            <Label htmlFor="inviteEmail" className="text-atrium-black/80">Invite Team Members (Optional)</Label>
            
            <div className="flex gap-2 justify-center items-center">
              <Input
                id="inviteEmail"
                value={currentEmail}
                onChange={(e) => setCurrentEmail(e.target.value)}
                placeholder="Enter email address"
                variant="white"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddEmail()
                  }
                }}
              />
              <Button
                type="button"
                variant="atrium"
                onClick={handleAddEmail}
                disabled={!currentEmail}
              >
                <HugeiconsIcon icon={Add01Icon} className="w-4 h-4" />
              </Button>
            </div>
            
            {inviteEmails.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-sm text-neutral-600">Invited members:</p>
                <div className="space-y-2">
                  {inviteEmails.map(email => (
                    <div key={email} className="flex justify-between items-center px-3 py-2 rounded-md bg-neutral-100">
                      <span className="text-sm text-atrium-black">{email}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveEmail(email)}
                        className="w-6 h-6 text-neutral-500 hover:text-atrium-black"
                      >
                        <HugeiconsIcon icon={Cancel01Icon} className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <p className="mt-2 text-sm text-neutral-600">
              You can invite team members now or add them later.
            </p>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col justify-center items-center w-full h-full">
      {/* Header with Progress */}
      <div className="px-6 pt-6 mb-4 w-full shrink-0">
        <div className="overflow-hidden w-full h-1 rounded-full bg-neutral-200">
          <div
            className="h-full bg-accent-600 transition-all duration-300"
            style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
          />
        </div>
        <div className="mt-4">
          <h2 className="text-lg font-semibold tracking-tight text-atrium-black">{STEPS[currentStep - 1]?.title || 'Step ' + currentStep}</h2>
          <p className="text-sm text-neutral-600">{STEPS[currentStep - 1]?.subtitle || ''}</p>
        </div>
      </div>

      {/* Content */}
      <div className="overflow-y-auto flex-1 p-6 w-full">
        {renderStepContent()}
      </div>

      {/* Footer */}
      <div className="flex sticky bottom-0 justify-between items-center px-4 pt-4 pb-6 w-full bg-white dark:bg-atrium-background border-t border-atrium-border shrink-0">
        <Button
          type="button"
          variant="ghost"
          onClick={currentStep === 1 ? onCancel : () => setCurrentStep(prev => prev - 1)}
          disabled={isLoading}
        >
          {currentStep === 1 ? 'Cancel' : 'Back'}
        </Button>
        <Button
          type="submit"
          variant="atrium"
          disabled={isLoading || showCropper || !isStepValid()}
        >
          {isLoading ? 'Creating...' : currentStep < STEPS.length ? 'Continue' : 'Create Workspace'}
        </Button>
      </div>
    </form>
  )
}
